import { FC, useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Typography,
} from "@material-ui/core";
import { useHistory } from "react-router";
import { AiFillWarning } from "react-icons/ai";

import PoolCardStyle from "./PoolsStyle";
import LandingBg from "../../assets/images/landing_bg.png";
import PoolIcon from "../../assets/images/GAINSFish.svg";
import PoolButton from "./PoolButton";
import PoolContext from "../../store/Pool/PoolContext";

import LinearProgressBar from "../../helpers/LinearProgress";
import WalletContext from "../../store/Wallet/WalletContext";
import { calculateProgress } from "../../helpers/PoolsProgressCalc";

import { USDollar } from "../../utils/currency/usd";
import { PoolRefundPolicy } from "../../constants/pool";
import GiveawayLabel from "./GiveawayLabel";
import { getNetworkBadge } from "../../utils/web3/getNetworkBadge";

interface PoolCardProps {
  refundPolicy?: PoolRefundPolicy;
  [key: string]: any;
}

// TODO: Update types and props
const PoolCard: FC<PoolCardProps> = ({
  id,
  slug,
  imgSrc,
  banner,
  title,
  description,
  totalRaise,
  status,
  kycDeadlineDate,
  pledgeStartDate,
  pledgeEndDate,
  contributionStartDate,
  networks,
  getWhitelisted,
  investBtn,
  whitelistedCompleted,
  quizResult,
  statusRef,
  distributionScAddress,
  refundPolicy,
  code,
}) => {

  const classes = PoolCardStyle();
  const history = useHistory();

  const { setPoolProgressDetails, poolProgress, getPoolProgressDetails, setActivePool, isGiveaway } = useContext<any>(PoolContext);
  const { getContributionDetails } = useContext<any>(WalletContext);

  const [failed, setFailed] = useState(false);
  const [showAlert, toggleAlert] = useState(false);
  const [poolProgressError, setPoolProgressError] = useState<any>(false);
  const [totalRaised, setTotalRaised] = useState(0);
  const [maxRaise, setMaxRaise] = useState(0);
  const [progressval, setProgressval] = useState(0);
  const [chipColor, setColor] = useState('default');
  const [isGiveawayPool, setGiveawayPool] = useState(false);

  const getStatusText = (status: string) => {
    switch (status) {
      case "ongoing":
        return "Live";
      case "whitelist_open":
        return "Whitelist Open";
      case "whitelist_closed":
        return "Whitelist Closed";
      case "upcoming":
        return "Upcoming";
      case "closed":
        return "Closed";
    }
  };

  useEffect(() => {
    if (isGiveaway(id)) {
      setGiveawayPool(true);
    }
  }, [])

  useEffect(() => {
    switch (status) {
      case "ongoing":
        setColor('green');
        return;
      case "whitelist_open":
        setColor('darkGreen');
        return;
      case "whitelist_closed":
        setColor('darkGreen');
        return;
      case "upcoming":
        setColor('orange');
        return;
      case "closed":
        setColor('darkGreen');
        return;
      case "cancelled":
        setColor('red');
        return;
      case "refunded":
        setColor('orange');
        return;
    }
  }, [])

  useEffect(() => {
    if (networks && networks.length > 0 && status === 'ongoing') {
      const network = networks[0]; // TODO: handle multiple networks - May never be multiple networks
      const { address, networkId, token } = network;
      getContributionDetails(networkId, address, token)
      .then((res: any) => {
        setPoolProgressDetails(id, res.cap, res.amount) 
      })
      .catch((err: any) => { 
        setPoolProgressError(true)
      })
    }
  }, [])

  useEffect(() => {
    if (maxRaise === 0) setProgressval(0);
    setProgressval(calculateProgress(totalRaised!, maxRaise!));
  }, [totalRaised, maxRaise])

  const handleClick = () => {
    setActivePool({ id, networks, name: title, slug })

    let urlId;
    if(slug){
      urlId = slug
    }else{
      urlId = id
    }

    let url = `/project/${urlId}` + (code ? `?code=${code}` : '');
    history.push(url, {
      investBtn,
      getWhitelisted,
      whitelistedCompleted,
      failed,
    });
  };

  useEffect(() => {
    if (status === 'whitelist_open' && getWhitelisted && whitelistedCompleted) {
      toggleAlert(true);
    }
    if (status === "whitelist_open" && quizResult?.length > 0) {
      let failedQuiz = quizResult[0]?.quizzes?.filter(
        (quiz: any) => quiz.numberOfAttempts === 3 && quiz.lastAttempt.isSuccessful === false
      );
      if (failedQuiz?.length > 0) setFailed(true);
    }
  }, []);

  useEffect(() => {
    if (poolProgress && poolProgress.length > 0) {
      const progress = getPoolProgressDetails(id);
      if (progress) {
        setTotalRaised(progress.amount ?? 0);
        setMaxRaise(progress.cap ?? 0);
      }
    }
  }, [poolProgress]);


  const { refundable } = refundPolicy ?? {};

  return (
    <Grid item lg={4} md={4} sm={6} xs={12}>
      <Card className={classes.root} variant="outlined" onClick={handleClick}>
        <CardActionArea>
          {
            isGiveawayPool && <GiveawayLabel />
          }
          <CardMedia
            image={banner ? banner : LandingBg}
            title="Live Pool"
            className={classes.media}
            component="img"
            height="160"
          />
          <Box className={classes.chip_wrapper}>
            <Chip
              label={getStatusText(status)}
              className={`${classes.chip} ${chipColor}`}
              ref={statusRef}
            />
            {refundable && (
              <Chip
                label="Refundable"
                className={`${classes.chip} yellow`}
                color="secondary"
              />
            )}
          </Box>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className={classes.iconWrapper}
          >
            <Grid item>
              <Avatar alt="Project Logo Avatar" src={imgSrc ? imgSrc : PoolIcon} className={classes.avatar} />
            </Grid>
            <Grid item>
              {
                distributionScAddress && distributionScAddress.length > 0 && (
                  <>
                    {
                      getNetworkBadge(distributionScAddress[0].chain) 
                      ? (
                        <Avatar
                          alt="Network"
                          src={getNetworkBadge(distributionScAddress[0].chain)}
                          className={classes.network}
                        />
                      ) : (
                        <>
                          <Chip className="fw-bold" color="primary" label={distributionScAddress[0].chain} />
                        </>
                      )
                    }
                  </>
                )
              }
            </Grid>
          </Grid>
          <CardContent className={classes.content}>
            <Typography
              gutterBottom
              variant="h5"
              component="h2"
              className={classes.title}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              component="p"
              className={classes.description}
            >
              {description}
            </Typography>
            <Grid
              container
              direction="column"
              className={classes.detailContainer}
            >
              <Grid item>
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item>
                    <Typography>
                      {
                        status === 'ongoing'
                        ? "Max Allocation"
                        : isGiveawayPool
                          ? "Rewards"
                          : "Total Raise"
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={true}>
                    <Box border={"1px #ECF0F3 dashed"} marginX={"18px"}></Box>
                  </Grid>
                  <Grid item>
                    <Typography>
                      <span className="detailValue">{`${USDollar.format(status === 'ongoing' ? maxRaise : totalRaise)}`}</span>
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {
                status === 'ongoing' && (
                  <Grid item>
                    <Grid
                      container
                      direction="column"
                      className={classes.progress}
                    >
                      <Grid item>
                        <Typography>Progress</Typography>
                      </Grid>
                      <Grid
                        item
                      >
                        {
                          (poolProgressError)
                            ?
                              <Typography component="span" variant="caption" color="secondary" className={classes.progressError}>
                                <AiFillWarning />&nbsp;Failed to read from blockchain
                              </Typography>
                            :
                              <LinearProgressBar value={progressval} maxRaise={maxRaise} totalRaised={totalRaised} className={classes.progressBar} showFull={true} />
                        }
                      </Grid>
                    </Grid>
                  </Grid>
                )
              }
            </Grid>
          </CardContent>
          <CardActions>
            <Grid
              container
              direction="row"
              alignItems="center"
              className={classes.actionContainer}
            >
              <PoolButton
                poolId={id}
                poolStatus={status}
                dealPage={true}
                handleClick={handleClick}
                whitelisted={getWhitelisted}
                whitelistedData={whitelistedCompleted}
                quizStatus={failed}
                kycDeadlineDate={kycDeadlineDate}
                invest={investBtn}
                pledgeStartDate={pledgeStartDate}
                pledgeEndDate={pledgeEndDate}
                contributionStartDate={contributionStartDate}
                fullWidth={false}
              />
            </Grid>
          </CardActions>
        </CardActionArea>
      </Card>
    </Grid>
  );
};

export default PoolCard;
